import { API } from '../actions/types';

/*
 * Builds the API action every request class returns. Nothing here talks to the
 * network -- middleware/api.js sniffs the action and does that.
 *
 * The query string shapes match rbmowatt/base's QueryParser:
 *   ?key=value            filter (must be in the service's $filterable)
 *   ?with[]=relation      eager load
 *   ?sort=name_ASC        sort (must be in $sortable or $sortScopes)
 *   ?limit=20&page=2      pagination, limit clamped to 100 server-side
 * An unlisted filter or sort key comes back 400, not an empty list.
 */
class BaseRequest {
  REQUEST_TYPE = 'BASE';

  endpoint = '';

  getEndpoint() {
    return this.endpoint;
  }

  /**
   * A label turns on API_START / API_END for this request so a component can
   * show a spinner keyed to it. Requests without one are silent.
   */
  getLabel(label, method) {
    return label === '' || label === undefined ? `${method}_${this.REQUEST_TYPE}` : label;
  }

  get({
    wheres = {},
    withs = [],
    limit = 20,
    page = 1,
    sort = '',
    label = '',
    onSuccess = () => ({ type: 'NOOP' }),
    onFailure = (e) => this.onFailure(e),
  } = {}) {
    return this.apiAction({
      url: `${this.getEndpoint()}?${this.toQueryString({ ...wheres, limit, page, sort }, withs)}`,
      onSuccess,
      onFailure,
      label: this.getLabel(label, 'GET'),
    });
  }

  getOne({
    id = null,
    withs = [],
    label = '',
    onSuccess = () => ({ type: 'NOOP' }),
    onFailure = (e) => this.onFailure(e),
  } = {}) {
    return this.apiAction({
      url: `${this.getEndpoint()}/${id}?${this.toQueryString({}, withs)}`,
      onSuccess,
      onFailure,
      label: this.getLabel(label, 'GET'),
    });
  }

  create({
    data = {},
    label = '',
    onSuccess = () => ({ type: 'NOOP' }),
    onFailure = (e) => this.onFailure(e),
  } = {}) {
    return this.apiAction({
      url: this.getEndpoint(),
      method: 'POST',
      data,
      onSuccess,
      onFailure,
      label: this.getLabel(label, 'POST'),
    });
  }

  update({
    id = null,
    data = {},
    label = '',
    onSuccess = () => ({ type: 'NOOP' }),
    onFailure = (e) => this.onFailure(e),
  } = {}) {
    return this.apiAction({
      url: `${this.getEndpoint()}/${id}`,
      method: 'PUT',
      data,
      onSuccess,
      onFailure,
      label: this.getLabel(label, 'PUT'),
    });
  }

  delete({
    id = null,
    label = '',
    onSuccess = () => ({ type: 'NOOP' }),
    onFailure = (e) => this.onFailure(e),
  } = {}) {
    return this.apiAction({
      url: `${this.getEndpoint()}/${id}`,
      method: 'DELETE',
      onSuccess,
      onFailure,
      label: this.getLabel(label, 'DELETE'),
    });
  }

  /**
   * Drops empty values so a cleared filter disappears from the URL instead of
   * being sent as `category_id=`, which the API reads as a real filter on the
   * empty string and answers with nothing.
   */
  toQueryString(params = {}, withs = []) {
    const qs = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') return;

      if (Array.isArray(value)) {
        qs.append(key, value.join(','));
        return;
      }

      qs.append(key, value);
    });

    withs.forEach((relation) => qs.append('with[]', relation));

    return qs.toString();
  }

  onFailure() {
    return { type: 'API_FAILED' };
  }

  apiAction({
    url = '',
    method = 'GET',
    data = null,
    onSuccess = () => ({ type: 'NOOP' }),
    onFailure = () => ({ type: 'NOOP' }),
    label = '',
  }) {
    return {
      type: API,
      payload: { url, method, data, onSuccess, onFailure, label },
    };
  }
}

export default BaseRequest;
